import type {
  AiAnalysis,
  ChatMeta,
  ChatRequest,
  EmotionEvent,
  ParentInsight,
  WeeklySummary
} from "./types";

type StreamHandlers = {
  onMeta: (meta: ChatMeta) => void;
  onToken: (token: string) => void;
  onDone: (meta?: ChatMeta) => void;
};

const parseSseEvent = (raw: string) => {
  const lines = raw.split("\n");
  let event = "message";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }

  const dataText = dataLines.join("\n");
  if (!dataText) return { event, data: null };

  try {
    return { event, data: JSON.parse(dataText) };
  } catch {
    return { event, data: dataText };
  }
};

export async function streamChat(request: ChatRequest, handlers: StreamHandlers) {
  const response = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request)
  });

  if (!response.ok || !response.body) {
    throw new Error("AI 服务暂时不可用");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";

    for (const part of parts) {
      const { event, data } = parseSseEvent(part);
      if (!data) continue;
      if (event === "meta") handlers.onMeta(data as ChatMeta);
      if (event === "token") handlers.onToken((data as { content: string }).content);
      if (event === "done") handlers.onDone((data as { meta?: ChatMeta }).meta);
    }
  }
}

export async function analyzeEmotion(text: string, source: "child" | "parent") {
  const response = await fetch("/api/analyze-emotion", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, source })
  });
  if (!response.ok) throw new Error("情绪分析失败");
  return response.json() as Promise<EmotionEvent>;
}

export async function createParentReport(payload: {
  messages: unknown[];
  emotionEvents: unknown[];
  childProfile: unknown;
}) {
  const response = await fetch("/api/parent-report", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!response.ok) throw new Error("家长报告生成失败");
  return response.json() as Promise<ParentInsight>;
}

export async function createWeeklySummary(payload: {
  messages: unknown[];
  emotionEvents: unknown[];
  childProfile: unknown;
}) {
  const response = await fetch("/api/weekly-summary", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!response.ok) throw new Error("周报生成失败");
  return response.json() as Promise<WeeklySummary>;
}

export async function riskCheck(text: string) {
  const response = await fetch("/api/risk-check", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text })
  });
  if (!response.ok) throw new Error("风险检测失败");
  return response.json() as Promise<AiAnalysis>;
}
